import 'view/css/home.css';

import React, { useEffect, useState } from 'react';
import moment from 'moment';

import API from 'service/api';
import { Data, hexToRgb, rgbToHsl, hslToRgb } from 'service/util';

import PopMessage from 'component/pop-msg/PopMessage';

import { Bar, Pie } from 'react-chartjs-2';
import Loading from 'component/Loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';

const prev1mo = moment().startOf('month').add(-1, 'month').format('MMM');
const prev2mo = moment().startOf('month').add(-2, 'month').format('MMM');

const colorsN = ['#e60049', '#0bb4ff', '#50e991', '#e6d800', '#9b19f5', '#ffa300', '#dc0ab4', '#b3d4ff', '#00bfa0'];
const colorsL = colorsN.map(hexToRgb)
  .map(x => rgbToHsl.apply(null, x))
  .map( ([h,s,l]) => [h,s,Math.min(1, l+.2)] )
  .map(x => hslToRgb.apply(null, x))
  .map(x => `#${x.map(b => `0${b.toString(16)}`.slice(-2)).join('')}`);

const getDiffColorN = (values, neg = false) => {
  return values.map(v => neg^(v > 0) ? colorsN[1]: colorsN[0]);
}
const getDiffColorL = (values, neg = false) => {
  return values.map(v => neg^(v > 0) ? colorsL[1]: colorsL[0]);
}


const HomeStatistics = (props) =>
{
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  const [serviceCount, setServiceCount] = useState(null);
  const [newCount, setNewCount]         = useState(null);
  const [termCount, setTermCount]       = useState(null);
  const [overdue, setOverdueCount]      = useState(null);
  const [sel_dis, setSelectedDistrict]  = useState(null);

  useEffect(() => {
    API.getHomeData().then(resp => {
      if (resp.success) {
        const { exSrv, newVh, termVh, overdue } = resp.msg;

        //--------------------------------------------
        // process new vehicle statistics
        let ixNewMap = newVh.reduce((r,x) => {
          if (!r.get(x.ix)) r.set(x.ix, {});
          r.get(x.ix)[x.company] = x.cnt;
          return r;
        }, new Map());

        const [p2mo, p1mo] = Array.from(ixNewMap.keys()).sort();
        let ncd = Data.company.reduce((r,com) => {
          let tCnt = ixNewMap.get(p1mo)?.[com.cd]||0;
          let dCnt = tCnt - ixNewMap.get(p2mo)?.[com.cd]||0;
          r.labels.push(com.name);
          r.tCnt.push(tCnt);
          r.dCnt.push(dCnt);
          return r;
        }, { labels: [], tCnt: [], dCnt: [] });

        //--------------------------------------------
        // process termination count
        let ixTermMap = termVh.reduce((r,x) => {
          if (!r.get(x.ix_yrmo)) r.set(x.ix_yrmo, {});
          let o = r.get(x.ix_yrmo);
          if (!o[x.company]) o[x.company] = { term: 0, susp: 0};
          o[x.company][x.is_terminate ? 'term':'susp'] = x.cnt;
          return r;
        }, new Map());

        let tcd = Data.company.reduce((r,com) => {
          let termCnt = ixTermMap.get(p1mo)?.[com.cd]?.term||0;
          let suspCnt = ixTermMap.get(p1mo)?.[com.cd]?.susp||0;
          let dtCnt = termCnt - ixTermMap.get(p2mo)?.[com.cd]?.term||0;
          let dsCnt = suspCnt - ixTermMap.get(p2mo)?.[com.cd]?.susp||0;
          r.labels.push(com.name);
          r.termCnt.push(termCnt);
          r.suspCnt.push(suspCnt);
          r.dtCnt.push(dtCnt);
          r.dsCnt.push(dsCnt);
          return r;
        }, { labels: [], termCnt: [], suspCnt: [], dtCnt: [], dsCnt: [] });

        //--------------------------------------------
        // overdue count
        let odMap = overdue.reduce((r,x) => {
          if (!r.get(x.mgt_dis)) r.set(x.mgt_dis, []);
          r.get(x.mgt_dis).push(x);
          return r;
        }, new Map());

        let ocd = Array.from(odMap.keys()).reduce((r,dist) => {
          // district
          let distData = odMap.get(dist);
          r.mgtDist.push(dist);
          r.labels.push(dist);
          r.tCnt.push(distData.reduce((v, x) => v+x.cnt, 0));
          r.aCnt.push(distData.reduce((v, x) => v+x.amount, 0));

          // location
          let locDataset = distData.sort((a,b) => {
            if (a.amount > b.amount) return -1;
            if (a.amount < b.amount) return  1;
            if (a.srv_loc > b.srv_loc) return  1;
            if (a.srv_loc < b.srv_loc) return -1;
            return 0;
          }).reduce((r, x) => {
            r.labels.push(`${x.srv_dis}${x.srv_loc} - ${x.loc_name}`);
            r.tCnt.push(x.cnt);
            r.aCnt.push(x.amount);
            return r;
          }, { labels: [], tCnt: [], aCnt: [] });
          r.loc.set(dist, locDataset);

          return r;
        }, { mgtDist: [], labels: [], tCnt: [], aCnt: [], loc: new Map() });

        setServiceCount(exSrv);
        setNewCount(ncd);
        setTermCount(tcd);
        setOverdueCount(ocd);
        setSelectedDistrict(Array.from(odMap.keys())[0]);

        setReady(true);
      } else {
        PopMessage.show(resp.msg, '讀取失敗', 'danger');
        setError(resp.msg);
      }
    });
  }, []);

  if (!ready) return <Loading fullscreen={true} />

  if (error) return <p className="message red">{ error }</p>

  const renderDiff = (v) => {
    if (v > 0) return <span><FontAwesomeIcon icon={faCaretUp} />{v}</span>;
    if (v < 0) return <span><FontAwesomeIcon icon={faCaretDown} />{Math.abs(v)}</span>;
    return '';
  }

  return (
    <div className="home-view">
      <section className="stat-section">
        <div className="existing-services">
          <h2>服務中的車輛數量： { serviceCount }</h2>
        </div>
      </section>
      <section className="stat-section">
        <h2>{prev2mo}及{prev1mo}新車數量比較</h2>
        <div className="new-vehicles">
          <div className="new-pie">
            <Pie options={{ responsive: true, maintainAspectRatio: false, plugins: {legend: { position: 'left' }} }}
              data={{
                labels: newCount.labels,
                datasets:[{
                  data: newCount.tCnt,
                  backgroundColor: colorsN
                }]
              }}
            />
          </div>
          <div className="new-bar">
            <Bar options={{ responsive: true, maintainAspectRatio: false }}
              data={{
                labels: newCount.labels,
                datasets:[{
                  label: '與上月比較變動',
                  data: newCount.dCnt,
                  barThickness: 30,
                  backgroundColor: getDiffColorN(newCount.dCnt)
                }]
              }}
            />
          </div>
          <div className="new-table">
            <table>
              <tbody>
                <tr>
                  <th>公司</th>
                  <th className="right">{ prev1mo }新車</th>
                  <th></th>
                </tr>
                { Array.from(Array(Data.company.length).keys()).map( i =>
                  <tr key={i}>
                    <td>{ newCount.labels[i] }</td>
                    <td className="right">{ newCount.tCnt[i] }</td>
                    <td className="cnt-diff">
                      { renderDiff(newCount.dCnt[i] ) }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section className="stat-section">
        <h2>{prev2mo}及{prev1mo}暫停及終止服務數量比較</h2>
        <div className="term-vehicles">
          <div className="term-pie">
            <Pie options={{ responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'left' },
                  title: { display: true, text: '終止服務數量' }
                }
              }}
              data={{
                labels: termCount.labels,
                datasets:[{
                  data: termCount.termCnt,
                  backgroundColor: colorsN
                }]
              }}
            />
          </div>
          <div className="term-pie">
            <Pie options={{ responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'left' },
                  title: { display: true, text: '暫停服務數量' }
                }
              }}
              data={{
                labels: termCount.labels,
                datasets:[{
                  data: termCount.suspCnt,
                  backgroundColor: colorsL
                }]
              }}
            />
          </div>
          <div className="term-bar">
            <Bar options={{ responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  title: { display: true, text: '終止及暫停服務與上月比較的變動' }
                }
              }}
              data={{
                labels: termCount.labels,
                datasets:[{
                  label: 'Δ終止服務',
                  data: termCount.dtCnt,
                  barThickness: 30,
                  backgroundColor: getDiffColorN(termCount.dtCnt, true)
                }, {
                  label: 'Δ暫停服務',
                  data: termCount.dsCnt,
                  barThickness: 30,
                  backgroundColor: getDiffColorL(termCount.dsCnt, true)
                }]
              }}
            />
          </div>
          <div className="term-table">
            <table>
              <tbody>
                <tr>
                  <th>公司</th>
                  <th className="right">{ prev1mo }終止服務</th>
                  <th></th>
                  <th className="right">{ prev1mo }暫停服務</th>
                  <th></th>
                </tr>
                { Array.from(Array(Data.company.length).keys()).map( i =>
                  <tr key={i}>
                    <td>{ termCount.labels[i] }</td>
                    <td className="right">{ termCount.termCnt[i] }</td>
                    <td className="cnt-diff">
                      { renderDiff(termCount.dtCnt[i] ) }
                    </td>
                    <td className="right">{ termCount.suspCnt[i] }</td>
                    <td className="cnt-diff">
                      { renderDiff(termCount.dsCnt[i] ) }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      <section className="stat-section">
        <h2>逾期賬單</h2>
        <p className="message blue">
        * 逾期賬單包括所有今日已超過繳款期限的所有未繳款賬單，繳款期限為應收費月份的最後一日，月結賬單側為收費月份下個月的最後一日。
        </p>
        <div className="overdue-count">
         <div className="overdue-pie-district amount">
            <Pie options={{ responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'left' },
                  title: { display: true, text: '各區截至上月月尾的逾期賬單金額' }
                },
                onClick: (e) => {
                  let [elm] = e.chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
                  if (elm) setSelectedDistrict( overdue.mgtDist[elm.index] );
                }
              }}
              data={{
                labels: overdue.labels,
                datasets:[{
                  data: overdue.aCnt,
                  backgroundColor: colorsN
                }]
              }}
            />
          </div>
          <div className="overdue-pie-district count">
            <Bar options={{ responsive: true, maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  title: { display: true, text: '各區截至上月月尾的逾期賬單數量' }
                },
                onClick: (e) => {
                  let [elm] = e.chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
                  if (elm) setSelectedDistrict( overdue.mgtDist[elm.index] );
                }
              }}
              data={{
                labels: overdue.labels,
                datasets:[{
                  label: '',
                  data: overdue.tCnt,
                  backgroundColor: colorsN
                }]
              }}
            />
          </div>
          { !!sel_dis && <>
            <div className="overdue-pie-location amount">
              <Bar options={{
                  responsive: true, maintainAspectRatio: false,
                  plugins: {
                    legend: { display: false },
                    title: { display: true, text: `管轄區${sel_dis}各屋苑/車場截至上月月尾的逾期賬單金額` }
                  }
                }}
                data={{
                  labels: overdue.loc.get(sel_dis).labels,
                  datasets:[{
                    data: overdue.loc.get(sel_dis).aCnt,
                    backgroundColor: colorsN
                  }]
                }}
              />
            </div>
          </>}
        </div>
      </section>
    </div>
  );

}

export default HomeStatistics;